import React from "react";
import { View, StyleSheet, StyleProp, ViewStyle } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";

type BadgeVariant = "accepted" | "pending" | "declined" | "active" | "completed" | "invited" | "ready" | "upcoming";

interface StatusBadgeProps {
  variant: BadgeVariant;
  text: string;
  icon?: keyof typeof MaterialCommunityIcons.glyphMap;
  style?: StyleProp<ViewStyle>;
  size?: "small" | "medium" | "large";
}

export function StatusBadge({ variant, text, icon, style, size = "medium" }: StatusBadgeProps) {
  const { colors } = useTheme();

  const variantStyles = {
    accepted: {
      backgroundColor: colors.emerald?.background || "#10b98180",
      borderColor: colors.emerald?.border || "#10b981",
      textColor: colors.emerald?.text || "#ffffff",
    },
    pending: {
      backgroundColor: colors.amber?.background || "#f59e0b80",
      borderColor: colors.amber?.border || "#f59e0b",
      textColor: colors.amber?.text || "#ffffff",
    },
    declined: {
      backgroundColor: colors.rose?.background || "#ef444480",
      borderColor: colors.rose?.border || "#ef4444",
      textColor: colors.rose?.text || "#ffffff",
    },
    active: {
      backgroundColor: colors.blue?.background || "#3b82f680",
      borderColor: colors.blue?.border || "#3b82f6",
      textColor: colors.blue?.text || "#ffffff",
    },
    completed: {
      backgroundColor: colors.emerald?.background || "#10b98180",
      borderColor: colors.emerald?.border || "#10b981",
      textColor: colors.emerald?.text || "#ffffff",
    },
    invited: {
      backgroundColor: colors.purple?.background || "#a855f780",
      borderColor: colors.purple?.border || "#a855f7",
      textColor: colors.purple?.text || "#ffffff",
    },
    ready: {
      backgroundColor: colors.blue?.background || "#3b82f680",
      borderColor: colors.blue?.border || "#3b82f6",
      textColor: colors.blue?.text || "#ffffff",
    },
    upcoming: {
      backgroundColor: colors.gray?.background || "#6b728080",
      borderColor: colors.gray?.border || "#6b7280",
      textColor: colors.gray?.text || "#ffffff",
    },
  };

  const sizeStyles = {
    small: {
      paddingHorizontal: 8,
      paddingVertical: 3,
      fontSize: 11,
      iconSize: 12,
    },
    medium: {
      paddingHorizontal: 10,
      paddingVertical: 5,
      fontSize: 13,
      iconSize: 14,
    },
    large: {
      paddingHorizontal: 14,
      paddingVertical: 7,
      fontSize: 15,
      iconSize: 16,
    },
  };

  const variant_style = variantStyles[variant] || variantStyles.pending; // Fallback to pending if variant not found
  const size_style = sizeStyles[size] || sizeStyles.medium; // Fallback to medium if size not found

  return (
    <View
      style={[
        styles.badge,
        {
          backgroundColor: variant_style.backgroundColor,
          borderColor: variant_style.borderColor,
          paddingHorizontal: size_style.paddingHorizontal,
          paddingVertical: size_style.paddingVertical,
        },
        style,
      ]}
    >
      {icon && (
        <MaterialCommunityIcons
          name={icon}
          size={size_style.iconSize}
          color={variant_style.textColor}
          style={{ marginRight: 4 }}
        />
      )}
      <ThemedText
        style={[
          styles.badgeText,
          {
            color: variant_style.textColor,
            fontSize: size_style.fontSize,
          },
        ]}
      >
        {text}
      </ThemedText>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    alignSelf: "flex-start",
    boxShadow: "0 1px 1.41px 0 rgba(0, 0, 0, 0.1)",
    elevation: 2,
  },
  badgeText: {
    fontWeight: "700",
    letterSpacing: 0.5,
  },
});
